import type { ThemePreference } from '../state/settings.svelte';

export type Theme = 'light' | 'dark';

/** Surface color per theme, for `<meta name="theme-color">` (must match --bg in app.css). */
export const THEME_COLOR: Record<Theme, string> = { light: '#f5f5f7', dark: '#17171a' };

export function resolveTheme(preference: ThemePreference, systemDark: boolean): Theme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

const QUERY = '(prefers-color-scheme: dark)';

/** Tracks `prefers-color-scheme` live. */
export class SystemTheme {
  dark: boolean;
  readonly #media: MediaQueryList | null;
  readonly #listener = (event: MediaQueryListEvent) => {
    this.dark = event.matches;
  };

  constructor(
    win: Pick<Window, 'matchMedia'> | null = typeof window === 'undefined' ? null : window,
  ) {
    this.#media = typeof win?.matchMedia === 'function' ? win.matchMedia(QUERY) : null;
    this.dark = $state(this.#media?.matches ?? false);
    this.#media?.addEventListener('change', this.#listener);
  }

  destroy(): void {
    this.#media?.removeEventListener('change', this.#listener);
  }
}

/** Applies a theme to the document: `data-theme` plus the browser UI color. */
export function applyTheme(doc: Document, theme: Theme): void {
  doc.documentElement.dataset.theme = theme;
  let meta = doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = doc.createElement('meta');
    meta.name = 'theme-color';
    doc.head.append(meta);
  }
  meta.content = THEME_COLOR[theme];
}
